import { REVIEW_TEST_IDS } from '@/components/admin/sessions/contracts';
import { useReviewApi } from '@/components/admin/sessions/review/review-context';
import { Button } from '@/components/ui/button';
import { type Passage, shortPassage } from '@/lib/review-transcript';
import { cn } from '@/lib/utils';
import { REVIEW_NOTE_MAX, type ReviewQuestionVerdict } from '@matter_extractor/shared';
import { useEffect, useRef, useState } from 'react';

/**
 * Annotating a question of the interviewer (relecture.md §7, D13): « Va » or « Ne
 * va pas », required, and a facultative note - on the whole message or on the
 * passage selected. Only the interviewer's messages are annotated, and a message
 * takes several annotations.
 */

export interface ReviewAnnotateFormProps {
  readonly turn: number;
  /** The passage selected, or null for the message entire. */
  readonly passage: Passage | null;
  /** The annotation was posed. */
  readonly onDone: () => void;
  readonly onCancel: () => void;
}

const VERDICTS: readonly { readonly id: ReviewQuestionVerdict; readonly label: string }[] = [
  { id: 'good', label: 'Va' },
  { id: 'bad', label: 'Ne va pas' },
];

export function ReviewAnnotateForm({ turn, passage, onDone, onCancel }: ReviewAnnotateFormProps) {
  const { apply, busy } = useReviewApi();
  const [verdict, setVerdict] = useState<ReviewQuestionVerdict | null>(null);
  const [error, setError] = useState<string | null>(null);
  const first = useRef<HTMLButtonElement>(null);
  const note = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    first.current?.focus({ preventScroll: true });
  }, []);

  const save = async (): Promise<void> => {
    if (verdict === null) {
      setError('Choisissez « Va » ou « Ne va pas ».');
      return;
    }
    const applied = await apply([
      {
        op: 'annotate-question',
        turn,
        passage:
          passage === null ? null : { start: passage.start, end: passage.end, text: passage.text },
        verdict,
        note: (note.current?.value ?? '').trim(),
      },
    ]);
    if (applied) onDone();
  };

  return (
    <>
      <p className="text-gray-600">
        Annoter{' '}
        {passage === null ? (
          'le message'
        ) : (
          <q className="text-gray-900">{shortPassage(passage.text, 70)}</q>
        )}{' '}
        (tour {turn})
      </p>
      <fieldset className="m-0 inline-flex min-w-0 self-start overflow-hidden rounded-md border border-gray-200 p-0">
        <legend className="sr-only">Verdict</legend>
        {VERDICTS.map((entry, at) => (
          <button
            key={entry.id}
            ref={at === 0 ? first : undefined}
            type="button"
            data-testid={REVIEW_TEST_IDS.verdict}
            data-verdict={entry.id}
            aria-pressed={verdict === entry.id}
            onClick={() => {
              setVerdict(entry.id);
              setError(null);
            }}
            className={cn(
              'cursor-pointer px-3 py-0.5 font-semibold text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              verdict === entry.id &&
                (entry.id === 'good' ? 'bg-vert text-white' : 'bg-rouge-deep text-white'),
            )}
          >
            {entry.label}
          </button>
        ))}
      </fieldset>
      <textarea
        ref={note}
        data-testid={REVIEW_TEST_IDS.annotationNote}
        aria-label="Note de l’annotation"
        placeholder="Ce qui va ou ne va pas (facultatif)"
        maxLength={REVIEW_NOTE_MAX}
        rows={2}
        className="min-h-14 w-full resize-y rounded-md border border-gray-300 px-2 py-1.5 text-sm focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <div className="flex flex-wrap items-center gap-1.5">
        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid={REVIEW_TEST_IDS.annotationSave}
          disabled={busy}
          className="h-7 px-2.5 text-caption"
          onClick={() => void save()}
        >
          Annoter
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 px-2.5 text-caption text-gray-700"
          onClick={onCancel}
        >
          Annuler
        </Button>
        {error === null ? null : (
          <span
            role="alert"
            data-testid={REVIEW_TEST_IDS.annotationError}
            className="text-rouge-deep"
          >
            {error}
          </span>
        )}
      </div>
    </>
  );
}
